import { useCallback, useEffect, useState } from "react";
import { UserCheck, UserX, Loader2, Plus, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, roleLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";

const timeSpan = (j) => {
  const t = (iso) => (iso ? String(iso).slice(11, 16) : "");
  return j.start ? `${t(j.start)}${j.end ? `–${t(j.end)}` : ""}` : "";
};

export const AvailabilityPanel = ({ date, onBook, refreshKey, dragActive, onDropJob }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [over, setOver] = useState(null);

  const dropProps = (r) =>
    dragActive && onDropJob
      ? {
          onDragOver: (e) => { e.preventDefault(); setOver(r.user_id); },
          onDragLeave: () => setOver((id) => (id === r.user_id ? null : id)),
          onDrop: (e) => { e.preventDefault(); setOver(null); onDropJob(r.user_id, r.name); },
        }
      : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/availability", { params: { date } });
      setRows(data.users || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const free = rows.filter((r) => r.available);
  const busy = rows.filter((r) => !r.available);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="availability-panel">
      <button
        data-testid="availability-toggle"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors duration-200 hover:bg-slate-50"
      >
        <h2 className="text-xl font-semibold text-slate-800">{t("availability.title")}</h2>
        <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-800" data-testid="availability-count">
          {loading ? "…" : free.length}
        </span>
        <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500" data-testid="availability-date">
          {date}
        </span>
        <ChevronDown
          className={`ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4">
      {loading ? (
        <Loader2 className="mt-3 h-4 w-4 animate-spin text-slate-400" />
      ) : (
        <>
          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.05em] text-green-700">
            <UserCheck className="h-3.5 w-3.5" /> {t("availability.free")} ({free.length})
          </p>
          {free.length === 0 && (
            <p className="mt-1 text-sm text-slate-500" data-testid="availability-none-free">
              {t("availability.noneFree")}
            </p>
          )}
          <div className="mt-1 space-y-1">
            {free.map((r) => (
              <div
                key={r.user_id}
                data-testid={`availability-free-${r.user_id}`}
                {...dropProps(r)}
                className={`flex items-center gap-2 rounded-md border p-2.5 transition-colors duration-200 ${
                  over === r.user_id ? "border-dashed border-blue-500 bg-blue-100" : "border-green-200 bg-green-50/60"
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900">{r.name}</span>
                  <span className="block text-xs text-slate-500">{roleLabel(r.role, t)}</span>
                </span>
                {onBook && (
                  <Button
                    data-testid={`availability-book-${r.user_id}`}
                    onClick={() => onBook(r.user_id)}
                    className="h-9 bg-blue-600 px-2.5 text-white transition-colors duration-200 hover:bg-blue-700"
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> {t("availability.book")}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {busy.length > 0 && (
            <>
              <p className="mt-4 flex items-center gap-1.5 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">
                <UserX className="h-3.5 w-3.5" /> {t("availability.busy")} ({busy.length})
              </p>
              <div className="mt-1 space-y-1">
                {busy.map((r) => (
                  <div
                    key={r.user_id}
                    data-testid={`availability-busy-${r.user_id}`}
                    {...dropProps(r)}
                    className={`rounded-md border p-2.5 transition-colors duration-200 ${
                      over === r.user_id ? "border-dashed border-blue-500 bg-blue-100" : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <span className="block truncate text-sm font-semibold text-slate-700">{r.name}</span>
                    <span className="block text-xs text-slate-500">
                      {r.absence
                        ? `${t(`absType.${r.absence.type}`)} · ${t(`absStatus.${r.absence.status}`)}`
                        : (r.conflicts || [])
                            .map((c) => `#${c.order_number || "?"} ${c.title} ${timeSpan(c)}`)
                            .join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
        </div>
      )}
    </div>
  );
};

export default AvailabilityPanel;
